import { NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SelectModule } from 'primeng/select';
import { TabsModule } from 'primeng/tabs';
import { LocalizationService } from '../../core/localization/localization.service';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { PaymentTermSelectorComponent } from '../../shared/design-system/payment-term-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { FormFieldErrorsState } from '../../shared/design-system/form-field-errors';
import {
  CustomerPartyLinkMode,
  mapCustomerTypeToPartyKind
} from '../platform/party-hub.utils';
import {
  FinanceApiService,
  OnboardCustomerPayload,
  OnboardCustomerProfilesPayload,
  PlatformCustomerAiProfileRecord,
  PlatformCustomerCommercialProfileRecord,
  PlatformCustomerComplianceProfileRecord,
  PlatformCustomerCreditProfileRecord,
  PlatformCustomerRecord,
  UpdatePlatformCustomerPayload
} from './finance-api.service';

type CustomerTypeOption = { value: number; labelKey: string };
type CustomerStatusOption = { value: number; labelKey: string };
type KindOption = { value: number; labelKey: string };
type IdentifierTypeOption = { value: number; labelKey: string };
type ContactTypeOption = { value: number; labelKey: string };

const CREATE_TABS = ['account', 'profiles'] as const;
const EDIT_TABS = ['account', 'identity', 'compliance', 'profiles', 'access'] as const;

@Component({
  standalone: true,
  selector: 'ain-finance-customer-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    NgTemplateOutlet,
    RouterLink,
    FormsModule,
    ButtonModule,
    CheckboxModule,
    InputTextModule,
    SelectModule,
    SelectButtonModule,
    TabsModule,
    CurrencySelectorComponent,
    PaymentTermSelectorComponent,
    PartySelectorComponent
  ],
  template: `
    <ain-page [title]="isCreateMode() ? t('finance.customers.form.createTitle') : t('finance.customers.form.editTitle')" [description]="t('finance.customers.detailDescription')">
      <ain-info-hint
        page-title-addon
        [titleKey]="isCreateMode() ? 'partyHub.help.customerCreate.title' : 'partyHub.help.customerEdit.title'"
        [descriptionKey]="isCreateMode() ? 'partyHub.help.customerCreate.description' : 'partyHub.help.customerEdit.description'"
        [bulletKeys]="isCreateMode() ? [
          'partyHub.help.customerCreate.bullets.partyLink',
          'partyHub.help.customerCreate.bullets.profiles',
          'partyHub.help.customerCreate.bullets.afterSave'
        ] : [
          'partyHub.help.customerEdit.bullets.identity',
          'partyHub.help.customerEdit.bullets.compliance',
          'partyHub.help.customerEdit.bullets.profiles'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/financial/payments/customers']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('common.save')" (click)="save()" [disabled]="saving()"></button>
        </div>

        @if (isCreateMode()) {
          <p-tabs [value]="activeTab()" (valueChange)="onTabChange($event)">
            <p-tablist>
              <p-tab value="account">{{ t('partyHub.tabs.account') }}</p-tab>
              <p-tab value="profiles">{{ t('partyHub.tabs.profilesOptional') }}</p-tab>
            </p-tablist>
            <p-tabpanels>
              <p-tabpanel value="account">
                <section class="card">
                  <div class="section-header">
                    <h3>{{ t('partyHub.tabs.account') }}</h3>
                    <ain-info-hint
                      [titleKey]="'partyHub.help.customerAccount.title'"
                      [descriptionKey]="'partyHub.help.customerAccount.description'"
                      [bulletKeys]="[
                        'partyHub.help.customerAccount.bullets.customerCode',
                        'partyHub.help.customerAccount.bullets.displayName',
                        'partyHub.help.customerAccount.bullets.type',
                        'partyHub.help.customerAccount.bullets.defaultCurrency',
                        'partyHub.help.customerAccount.bullets.defaultPaymentTerm',
                        'partyHub.help.customerAccount.bullets.status'
                      ]" />
                  </div>
                  <div class="grid">
                    <ain-form-field [label]="t('finance.customers.fields.customerCode')" [errorKey]="fieldErrors.errorKey('customerCode')">
                      <input
                        pInputText
                        [(ngModel)]="form.customerCode"
                        (ngModelChange)="fieldErrors.clear('customerCode')"
                        [class.p-invalid]="fieldErrors.has('customerCode')" />
                    </ain-form-field>
                    <ain-form-field [label]="t('finance.customers.fields.displayName')" [errorKey]="fieldErrors.errorKey('displayName')">
                      <input
                        pInputText
                        [(ngModel)]="form.displayName"
                        (ngModelChange)="fieldErrors.clear('displayName')"
                        [class.p-invalid]="fieldErrors.has('displayName')" />
                    </ain-form-field>
                    <label>
                      <span>{{ t('finance.customers.fields.type') }}</span>
                      <p-select class="field-select" [options]="typeOptions" optionValue="value" [(ngModel)]="form.type" (ngModelChange)="onCustomerTypeChanged($event)" appendTo="body">
                        <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                        <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                      </p-select>
                    </label>
                    <ain-form-field [label]="t('finance.customers.fields.defaultCurrencyCode')" [errorKey]="fieldErrors.errorKey('defaultCurrencyCode')">
                      <ain-currency-selector
                        [(ngModel)]="form.defaultCurrencyCode"
                        (ngModelChange)="fieldErrors.clear('defaultCurrencyCode')" />
                    </ain-form-field>
                    <label>
                      <span>{{ t('finance.customers.fields.defaultPaymentTermId') }}</span>
                      <ain-payment-term-selector [(ngModel)]="form.defaultPaymentTermId" />
                    </label>
                  </div>
                </section>

                <section class="card">
                  <div class="section-header">
                    <h3>{{ t('finance.customers.partyLink.sectionTitle') }}</h3>
                    <ain-info-hint
                      [titleKey]="'partyHub.help.customerPartyLink.title'"
                      [descriptionKey]="'partyHub.help.customerPartyLink.description'"
                      [bulletKeys]="[
                        'partyHub.help.customerPartyLink.bullets.auto',
                        'partyHub.help.customerPartyLink.bullets.link',
                        'partyHub.help.customerPartyLink.bullets.inline'
                      ]" />
                  </div>
                  <p-selectButton
                    [options]="partyLinkModeOptions()"
                    optionLabel="label"
                    optionValue="value"
                    [(ngModel)]="partyLinkMode" />

                  @if (partyLinkMode === 1) {
                    <ain-form-field class="full" [label]="t('finance.customers.fields.partyId')" [errorKey]="fieldErrors.errorKey('linkPartyId')">
                      <ain-party-selector
                        [(ngModel)]="linkPartyId"
                        (ngModelChange)="fieldErrors.clear('linkPartyId')" />
                    </ain-form-field>
                  }

                  @if (partyLinkMode === 2) {
                    <div class="grid">
                      <label>
                        <span>{{ t('platform.parties.fields.legalName') }}</span>
                        <input pInputText [(ngModel)]="inlineParty.legalName" (ngModelChange)="onInlineLegalNameChanged($event)" />
                      </label>
                      <label>
                        <span>{{ t('platform.parties.fields.kind') }}</span>
                        <p-select class="field-select" [options]="inlineKindOptions" optionValue="value" [(ngModel)]="inlineParty.kind" appendTo="body">
                          <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                          <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                        </p-select>
                      </label>
                      <label class="checkbox-row full">
                        <p-checkbox binary="true" [(ngModel)]="syncDisplayNameFromLegalName" />
                        <span>{{ t('finance.customers.partyLink.syncDisplayName') }}</span>
                      </label>
                      <label>
                        <span>{{ t('platform.parties.identifiers.type') }}</span>
                        <p-select class="field-select" [options]="identifierTypeOptions" optionValue="value" [(ngModel)]="inlineParty.identifierType" appendTo="body">
                          <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                          <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                        </p-select>
                      </label>
                      <label>
                        <span>{{ t('platform.parties.identifiers.value') }}</span>
                        <input pInputText [(ngModel)]="inlineParty.identifierValue" />
                      </label>
                      <label>
                        <span>{{ t('platform.parties.identifiers.country') }}</span>
                        <input pInputText [(ngModel)]="inlineParty.identifierCountryCode" />
                      </label>
                      <label>
                        <span>{{ t('platform.parties.contactPoints.type') }}</span>
                        <p-select class="field-select" [options]="contactTypeOptions" optionValue="value" [(ngModel)]="inlineParty.contactType" appendTo="body">
                          <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                          <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                        </p-select>
                      </label>
                      <label>
                        <span>{{ t('platform.parties.contactPoints.value') }}</span>
                        <input pInputText [(ngModel)]="inlineParty.contactValue" />
                      </label>
                    </div>
                  }
                </section>
              </p-tabpanel>

              <p-tabpanel value="profiles">
                <section class="card">
                  <ain-info-hint
                    [titleKey]="'partyHub.help.customerProfilesOptional.title'"
                    [descriptionKey]="'partyHub.help.customerProfilesOptional.description'"
                    [bulletKeys]="['partyHub.help.customerProfilesOptional.bullets.skip']" />
                  <ng-container *ngTemplateOutlet="profilesSections" />
                </section>
              </p-tabpanel>
            </p-tabpanels>
          </p-tabs>
        } @else if (currentCustomer()) {
          <p-tabs [value]="activeTab()" (valueChange)="onTabChange($event)">
            <p-tablist>
              <p-tab value="account">{{ t('partyHub.tabs.account') }}</p-tab>
              <p-tab value="identity">{{ t('partyHub.tabs.identity') }}</p-tab>
              <p-tab value="compliance">{{ t('partyHub.tabs.compliance') }}</p-tab>
              <p-tab value="profiles">{{ t('partyHub.tabs.profiles') }}</p-tab>
              <p-tab value="access">{{ t('partyHub.tabs.access') }}</p-tab>
            </p-tablist>
            <p-tabpanels>
              <p-tabpanel value="account">
                <section class="card">
                  <div class="section-header">
                    <h3>{{ t('partyHub.tabs.account') }}</h3>
                    <ain-info-hint
                      [titleKey]="'partyHub.help.customerAccount.title'"
                      [descriptionKey]="'partyHub.help.customerAccount.description'"
                      [bulletKeys]="[
                        'partyHub.help.customerAccount.bullets.customerCode',
                        'partyHub.help.customerAccount.bullets.displayName',
                        'partyHub.help.customerAccount.bullets.type',
                        'partyHub.help.customerAccount.bullets.defaultCurrency',
                        'partyHub.help.customerAccount.bullets.defaultPaymentTerm',
                        'partyHub.help.customerAccount.bullets.status'
                      ]" />
                  </div>
                  <div class="grid">
                    <ain-form-field [label]="t('finance.customers.fields.customerCode')">
                      <input pInputText [(ngModel)]="form.customerCode" readonly />
                    </ain-form-field>
                    <ain-form-field [label]="t('finance.customers.fields.displayName')" [errorKey]="fieldErrors.errorKey('displayName')">
                      <input
                        pInputText
                        [(ngModel)]="form.displayName"
                        (ngModelChange)="fieldErrors.clear('displayName')"
                        [class.p-invalid]="fieldErrors.has('displayName')" />
                    </ain-form-field>
                    <label>
                      <span>{{ t('finance.customers.fields.type') }}</span>
                      <p-select class="field-select" [options]="typeOptions" optionValue="value" [(ngModel)]="form.type" appendTo="body">
                        <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                        <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                      </p-select>
                    </label>
                    <ain-form-field [label]="t('finance.customers.fields.defaultCurrencyCode')" [errorKey]="fieldErrors.errorKey('defaultCurrencyCode')">
                      <ain-currency-selector
                        [(ngModel)]="form.defaultCurrencyCode"
                        (ngModelChange)="fieldErrors.clear('defaultCurrencyCode')" />
                    </ain-form-field>
                    <label>
                      <span>{{ t('finance.customers.fields.defaultPaymentTermId') }}</span>
                      <ain-payment-term-selector [(ngModel)]="form.defaultPaymentTermId" />
                    </label>
                    <label>
                      <span>{{ t('columns.status') }}</span>
                      <p-select
                        class="field-select"
                        [options]="statusOptions"
                        optionValue="value"
                        [(ngModel)]="form.status"
                        appendTo="body">
                        <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                        <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                      </p-select>
                    </label>
                  </div>
                </section>
              </p-tabpanel>

              <p-tabpanel value="identity">
                <section class="card">
                  @if (!partySummary()) {
                    <p>{{ t('finance.customers.compliance.partyNotLinked') }}</p>
                  } @else {
                    <div class="grid readonly-grid">
                      <label>
                        <span>{{ t('platform.parties.fields.displayName') }}</span>
                        <input pInputText readonly [value]="partySummary()!.displayName" />
                      </label>
                      <label>
                        <span>{{ t('platform.parties.fields.legalName') }}</span>
                        <input pInputText readonly [value]="partySummary()!.legalName || '-'" />
                      </label>
                      <label>
                        <span>{{ t('platform.parties.fields.kind') }}</span>
                        <input pInputText readonly [value]="kindLabel(partySummary()!.kind)" />
                      </label>
                      <label>
                        <span>{{ t('columns.status') }}</span>
                        <input pInputText readonly [value]="partyStatusLabel(partySummary()!.status)" />
                      </label>
                      @if (partySummary()!.primaryIdentifierValue) {
                        <label>
                          <span>{{ t('platform.parties.identifiers.value') }}</span>
                          <input pInputText readonly [value]="partySummary()!.primaryIdentifierValue!" />
                        </label>
                      }
                      @if (partySummary()!.primaryContactValue) {
                        <label>
                          <span>{{ t('platform.parties.contactPoints.value') }}</span>
                          <input pInputText readonly [value]="partySummary()!.primaryContactValue!" />
                        </label>
                      }
                    </div>
                    <a class="party-link" [routerLink]="['/platform/parties', partySummary()!.id]">
                      {{ t('partyHub.actions.openParty') }}
                    </a>
                  }
                </section>
              </p-tabpanel>

              <p-tabpanel value="compliance">
                <section class="card">
                  @if (!currentCustomer()?.partyId) {
                    <p class="gate-hint">{{ t('finance.customers.compliance.partyNotLinked') }}</p>
                  } @else if (!complianceProfile()) {
                    <p>{{ t('common.noRecords') }}</p>
                  } @else {
                    <p class="gate-hint" [class.allowed]="complianceProfile()?.allowsRegulatedOperation">
                      {{
                        complianceProfile()?.allowsRegulatedOperation
                          ? t('finance.customers.compliance.regulatedAllowed')
                          : t('finance.customers.compliance.regulatedBlocked')
                      }}
                    </p>
                    <div class="grid readonly-grid">
                      @for (field of complianceFields; track field.key) {
                        <label>
                          <span>{{ t(field.labelKey) }}</span>
                          <input pInputText readonly [value]="complianceStatusLabel(complianceProfile()![field.key])" />
                        </label>
                      }
                    </div>
                    <a class="party-link" [routerLink]="['/platform/parties', currentCustomer()?.partyId]" [queryParams]="{ tab: 'compliance' }">
                      {{ t('finance.customers.compliance.manageOnParty') }}
                    </a>
                  }
                </section>
              </p-tabpanel>

              <p-tabpanel value="profiles">
                <ng-container *ngTemplateOutlet="profilesSections" />
              </p-tabpanel>

              <p-tabpanel value="access">
                <section class="card">
                  <p>{{ t('partyHub.help.customerAccess.description') }}</p>
                  <a
                    class="party-link"
                    [routerLink]="['/platform/user-party-bindings']"
                    [queryParams]="{ customerAccountId: currentCustomer()?.id }">
                    {{ t('partyHub.actions.openUserPartyBindings') }}
                  </a>
                </section>
              </p-tabpanel>
            </p-tabpanels>
          </p-tabs>
        }

        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      }
    </ain-page>

    <ng-template #profilesSections>
      <section class="card profile-block">
        <div class="section-header">
          <h3>{{ t('finance.customers.commercial.title') }}</h3>
          <p>{{ t('finance.customers.commercial.description') }}</p>
        </div>
        <div class="grid">
          <label>
            <span>{{ t('finance.customers.commercial.priceTier') }}</span>
            <input pInputText [(ngModel)]="commercialForm.priceTier" />
          </label>
          <label>
            <span>{{ t('finance.customers.commercial.discountCeilingPercent') }}</span>
            <input pInputText type="number" [(ngModel)]="commercialForm.discountCeilingPercent" />
          </label>
          <label>
            <span>{{ t('finance.customers.commercial.riskBand') }}</span>
            <input pInputText [(ngModel)]="commercialForm.riskBand" />
          </label>
          <label class="full">
            <span>{{ t('finance.customers.commercial.paymentBehaviorNotes') }}</span>
            <input pInputText [(ngModel)]="commercialForm.paymentBehaviorNotes" />
          </label>
        </div>
        @if (!isCreateMode()) {
          <button pButton type="button" size="small" [label]="t('finance.customers.commercial.save')" (click)="saveCommercialProfile()" [disabled]="profileSaving()"></button>
        }
      </section>

      <section class="card profile-block">
        <div class="section-header">
          <h3>{{ t('finance.customers.credit.title') }}</h3>
          <p>{{ t('finance.customers.credit.description') }}</p>
        </div>
        <div class="grid">
          <label>
            <span>{{ t('finance.customers.credit.creditLimit') }}</span>
            <input pInputText type="number" [(ngModel)]="creditForm.creditLimit" />
          </label>
          <label>
            <span>{{ t('finance.customers.credit.exposureAmount') }}</span>
            <input pInputText type="number" [(ngModel)]="creditForm.exposureAmount" />
          </label>
          <label>
            <span>{{ t('finance.customers.credit.riskScore') }}</span>
            <input pInputText type="number" [(ngModel)]="creditForm.riskScore" />
          </label>
          <label class="checkbox-row">
            <p-checkbox binary="true" [(ngModel)]="creditForm.isOverdue" />
            <span>{{ t('finance.customers.credit.isOverdue') }}</span>
          </label>
        </div>
        @if (!isCreateMode()) {
          <button pButton type="button" size="small" [label]="t('finance.customers.credit.save')" (click)="saveCreditProfile()" [disabled]="profileSaving()"></button>
        }
      </section>

      <section class="card profile-block">
        <div class="section-header">
          <h3>{{ t('finance.customers.ai.title') }}</h3>
          <p>{{ t('finance.customers.ai.description') }}</p>
        </div>
        <div class="grid">
          <label>
            <span>{{ t('finance.customers.ai.churnScore') }}</span>
            <input pInputText type="number" [(ngModel)]="aiForm.churnScore" />
          </label>
          <label>
            <span>{{ t('finance.customers.ai.clvEstimate') }}</span>
            <input pInputText type="number" [(ngModel)]="aiForm.clvEstimate" />
          </label>
          <label>
            <span>{{ t('finance.customers.ai.propensityBand') }}</span>
            <input pInputText [(ngModel)]="aiForm.propensityBand" />
          </label>
          <label class="full">
            <span>{{ t('finance.customers.ai.nextBestActionNotes') }}</span>
            <input pInputText [(ngModel)]="aiForm.nextBestActionNotes" />
          </label>
        </div>
        @if (!isCreateMode()) {
          <button pButton type="button" size="small" [label]="t('finance.customers.ai.save')" (click)="saveAiProfile()" [disabled]="profileSaving()"></button>
        }
      </section>
    </ng-template>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .back-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; }
    .profile-block { margin-top: 0; }
    .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    label.full, ain-form-field.full { grid-column: 1 / -1; }
    .field-select { width: 100%; }
    .section-header { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 10px; }
    .section-header h3 { margin: 0; font-size: 1rem; }
    .section-header p { margin: 0 0 10px; color: #6b7280; font-size: 0.875rem; }
    .gate-hint { margin: 0 0 10px; font-weight: 600; color: #b91c1c; }
    .gate-hint.allowed { color: #15803d; }
    .party-link { display: inline-block; margin-top: 10px; color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .checkbox-row { display: flex; align-items: center; gap: 8px; align-self: end; }
    .message.error { color: #b91c1c; }
  `]
})
export class FinanceCustomerDetailComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly fieldErrors = new FormFieldErrorsState();
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly profileSaving = signal(false);
  readonly errorMessage = signal('');
  readonly currentCustomer = signal<PlatformCustomerRecord | null>(null);
  readonly complianceProfile = signal<PlatformCustomerComplianceProfileRecord | null>(null);
  readonly commercialProfile = signal<PlatformCustomerCommercialProfileRecord | null>(null);
  readonly creditProfile = signal<PlatformCustomerCreditProfileRecord | null>(null);
  readonly aiProfile = signal<PlatformCustomerAiProfileRecord | null>(null);
  readonly isCreateMode = signal(false);
  readonly activeTab = signal<string>('account');

  partyLinkMode: CustomerPartyLinkMode = 0;
  linkPartyId = '';
  syncDisplayNameFromLegalName = true;

  readonly statusOptions: CustomerStatusOption[] = [
    { value: 0, labelKey: 'finance.customers.status.draft' },
    { value: 1, labelKey: 'finance.customers.status.active' },
    { value: 2, labelKey: 'finance.customers.status.suspended' },
    { value: 3, labelKey: 'finance.customers.status.closed' }
  ];

  readonly typeOptions: CustomerTypeOption[] = [
    { value: 0, labelKey: 'finance.customers.type.b2c' },
    { value: 1, labelKey: 'finance.customers.type.b2b' },
    { value: 2, labelKey: 'finance.customers.type.government' },
    { value: 3, labelKey: 'finance.customers.type.dealer' },
    { value: 4, labelKey: 'finance.customers.type.distributor' },
    { value: 5, labelKey: 'finance.customers.type.broker' },
    { value: 6, labelKey: 'finance.customers.type.insured' },
    { value: 7, labelKey: 'finance.customers.type.fxClient' },
    { value: 8, labelKey: 'finance.customers.type.other' }
  ];

  readonly inlineKindOptions: KindOption[] = [
    { value: 0, labelKey: 'platform.parties.kind.individual' },
    { value: 1, labelKey: 'platform.parties.kind.organization' }
  ];

  readonly identifierTypeOptions: IdentifierTypeOption[] = [
    { value: 0, labelKey: 'platform.parties.identifierType.nationalId' },
    { value: 1, labelKey: 'platform.parties.identifierType.taxId' },
    { value: 2, labelKey: 'platform.parties.identifierType.registrationNumber' },
    { value: 3, labelKey: 'platform.parties.identifierType.passportNumber' },
    { value: 4, labelKey: 'platform.parties.identifierType.brokerCode' },
    { value: 5, labelKey: 'platform.parties.identifierType.other' }
  ];

  readonly contactTypeOptions: ContactTypeOption[] = [
    { value: 0, labelKey: 'platform.parties.contactType.email' },
    { value: 1, labelKey: 'platform.parties.contactType.phone' },
    { value: 2, labelKey: 'platform.parties.contactType.mobile' },
    { value: 3, labelKey: 'platform.parties.contactType.address' },
    { value: 4, labelKey: 'platform.parties.contactType.other' }
  ];

  readonly complianceFields = [
    { key: 'kycStatus' as const, labelKey: 'finance.customers.compliance.kycStatus' },
    { key: 'kybStatus' as const, labelKey: 'finance.customers.compliance.kybStatus' },
    { key: 'amlStatus' as const, labelKey: 'finance.customers.compliance.amlStatus' },
    { key: 'sanctionsStatus' as const, labelKey: 'finance.customers.compliance.sanctionsStatus' },
    { key: 'suitabilityStatus' as const, labelKey: 'finance.customers.compliance.suitabilityStatus' }
  ];

  form = {
    customerCode: '',
    displayName: '',
    type: 0,
    status: 1,
    defaultCurrencyCode: 'IRR',
    defaultPaymentTermId: null as string | null
  };

  inlineParty = {
    legalName: '',
    kind: 0,
    identifierType: 0,
    identifierValue: '',
    identifierCountryCode: '',
    contactType: 0,
    contactValue: ''
  };

  commercialForm = {
    priceTier: '',
    discountCeilingPercent: '' as number | string,
    riskBand: '',
    paymentBehaviorNotes: ''
  };

  creditForm = {
    creditLimit: '' as number | string,
    exposureAmount: '' as number | string,
    riskScore: '' as number | string,
    isOverdue: false
  };

  aiForm = {
    churnScore: '' as number | string,
    clvEstimate: '' as number | string,
    propensityBand: '',
    nextBestActionNotes: ''
  };

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const tab = params.get('tab');
      const allowed = this.isCreateMode() ? CREATE_TABS : EDIT_TABS;
      if (tab && (allowed as readonly string[]).includes(tab)) {
        this.activeTab.set(tab);
      }
    });

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.loadFromRoute(params.get('customerId'));
    });
  }

  private loadFromRoute(customerId: string | null): void {
    const isCreate = !customerId || customerId === 'new';
    this.isCreateMode.set(isCreate);
    this.errorMessage.set('');

    if (isCreate) {
      this.currentCustomer.set(null);
      this.complianceProfile.set(null);
      this.commercialProfile.set(null);
      this.creditProfile.set(null);
      this.aiProfile.set(null);
      if (!(CREATE_TABS as readonly string[]).includes(this.activeTab())) {
        this.activeTab.set('account');
      }
      this.inlineParty.kind = mapCustomerTypeToPartyKind(this.form.type);
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    if (!(EDIT_TABS as readonly string[]).includes(this.activeTab())) {
      this.activeTab.set('account');
    }

    this.api.getPlatformCustomer(customerId).subscribe({
      next: customer => {
        this.currentCustomer.set(customer);
        this.form = {
          customerCode: customer.customerCode,
          displayName: customer.displayName,
          type: this.normalizeNumeric(customer.type),
          status: this.normalizeNumeric(customer.status),
          defaultCurrencyCode: customer.defaultCurrencyCode,
          defaultPaymentTermId: customer.defaultPaymentTermId ?? null
        };
        this.loadComplianceProfile(customer.id);
        this.loadCustomerProfiles(customer.id);
        this.loading.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('common.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  partySummary() {
    return this.currentCustomer()?.party ?? null;
  }

  onTabChange(tab: string | number | undefined): void {
    if (tab === undefined || tab === null) {
      return;
    }

    const key = String(tab);
    this.activeTab.set(key);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: key },
      queryParamsHandling: 'merge'
    });
  }

  onCustomerTypeChanged(type: number): void {
    this.inlineParty.kind = mapCustomerTypeToPartyKind(type);
  }

  onInlineLegalNameChanged(value: string): void {
    if (this.syncDisplayNameFromLegalName) {
      this.form.displayName = value;
    }
  }

  partyLinkModeOptions(): Array<{ label: string; value: CustomerPartyLinkMode }> {
    return [
      { label: this.t('finance.customers.partyLink.mode.auto'), value: 0 },
      { label: this.t('finance.customers.partyLink.mode.link'), value: 1 },
      { label: this.t('finance.customers.partyLink.mode.inline'), value: 2 }
    ];
  }

  save(): void {
    this.errorMessage.set('');
    const rules = [
      { field: 'displayName', value: () => this.form.displayName, messageKey: 'common.validation.required' },
      { field: 'defaultCurrencyCode', value: () => this.form.defaultCurrencyCode, messageKey: 'common.validation.required' }
    ];
    if (this.isCreateMode()) {
      rules.unshift({ field: 'customerCode', value: () => this.form.customerCode, messageKey: 'common.validation.required' });
    }
    if (this.isCreateMode() && this.partyLinkMode === 1) {
      rules.push({
        field: 'linkPartyId',
        value: () => this.linkPartyId,
        messageKey: 'finance.customers.partyLink.messages.linkPartyRequired'
      });
    }
    if (!this.fieldErrors.validate(rules)) {
      this.activeTab.set('account');
      return;
    }

    const customerCode = this.form.customerCode.trim();
    const displayName = this.form.displayName.trim();
    const defaultCurrencyCode = this.form.defaultCurrencyCode.trim();

    this.saving.set(true);

    if (this.isCreateMode()) {
      const payload: OnboardCustomerPayload = {
        customerCode,
        displayName,
        type: this.form.type,
        defaultCurrencyCode,
        defaultPaymentTermId: this.form.defaultPaymentTermId?.trim() || undefined,
        partyLinkMode: this.partyLinkMode,
        partyId: this.partyLinkMode === 1 ? this.linkPartyId.trim() : undefined,
        inlineParty: this.partyLinkMode === 2 ? this.buildInlinePartyPayload() : undefined,
        profiles: this.buildOnboardProfilesPayload()
      };
      this.api.onboardPlatformCustomer(payload).subscribe({
        next: created => {
          this.saving.set(false);
          void this.router.navigate(['/financial/payments/customers', created.id]);
        },
        error: () => {
          this.errorMessage.set(this.t('common.saveFailed'));
          this.saving.set(false);
        }
      });
      return;
    }

    const current = this.currentCustomer();
    if (!current) {
      this.errorMessage.set(this.t('common.saveFailed'));
      this.saving.set(false);
      return;
    }

    const payload: UpdatePlatformCustomerPayload = {
      displayName,
      type: this.form.type,
      status: this.form.status,
      defaultCurrencyCode,
      defaultPaymentTermId: this.form.defaultPaymentTermId?.trim() || undefined,
      partyId: current.partyId
    };
    this.api.updatePlatformCustomer(current.id, payload).subscribe({
      next: updated => {
        this.currentCustomer.set(updated);
        this.form.status = this.normalizeNumeric(updated.status);
        this.saving.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('common.saveFailed'));
        this.saving.set(false);
      }
    });
  }

  saveCommercialProfile(): void {
    const current = this.currentCustomer();
    if (!current) {
      return;
    }

    this.profileSaving.set(true);
    this.errorMessage.set('');

    this.api.updatePlatformCustomerCommercialProfile(current.id, {
      priceTier: this.commercialForm.priceTier.trim() || undefined,
      discountCeilingPercent: this.toOptionalNumber(this.commercialForm.discountCeilingPercent),
      riskBand: this.commercialForm.riskBand.trim() || undefined,
      paymentBehaviorNotes: this.commercialForm.paymentBehaviorNotes.trim() || undefined
    }).subscribe({
      next: profile => {
        this.commercialProfile.set(profile);
        this.profileSaving.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('common.saveFailed'));
        this.profileSaving.set(false);
      }
    });
  }

  saveCreditProfile(): void {
    const current = this.currentCustomer();
    if (!current) {
      return;
    }

    this.profileSaving.set(true);
    this.errorMessage.set('');

    this.api.updatePlatformCustomerCreditProfile(current.id, {
      creditLimit: this.toOptionalNumber(this.creditForm.creditLimit),
      exposureAmount: this.toOptionalNumber(this.creditForm.exposureAmount),
      isOverdue: this.creditForm.isOverdue,
      riskScore: this.toOptionalNumber(this.creditForm.riskScore)
    }).subscribe({
      next: profile => {
        this.creditProfile.set(profile);
        this.profileSaving.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('common.saveFailed'));
        this.profileSaving.set(false);
      }
    });
  }

  saveAiProfile(): void {
    const current = this.currentCustomer();
    if (!current) {
      return;
    }

    this.profileSaving.set(true);
    this.errorMessage.set('');

    this.api.updatePlatformCustomerAiProfile(current.id, {
      churnScore: this.toOptionalNumber(this.aiForm.churnScore),
      clvEstimate: this.toOptionalNumber(this.aiForm.clvEstimate),
      propensityBand: this.aiForm.propensityBand.trim() || undefined,
      nextBestActionNotes: this.aiForm.nextBestActionNotes.trim() || undefined
    }).subscribe({
      next: profile => {
        this.aiProfile.set(profile);
        this.profileSaving.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('common.saveFailed'));
        this.profileSaving.set(false);
      }
    });
  }

  complianceStatusLabel(value: number | string | undefined): string {
    if (typeof value === 'undefined') {
      return '-';
    }

    const numeric = typeof value === 'string' ? Number(value) : value;
    const map: Record<number, string> = {
      0: this.t('finance.customers.compliance.status.notStarted'),
      1: this.t('finance.customers.compliance.status.pending'),
      2: this.t('finance.customers.compliance.status.passed'),
      3: this.t('finance.customers.compliance.status.failed'),
      4: this.t('finance.customers.compliance.status.waived')
    };
    return map[numeric] ?? `${value}`;
  }

  statusLabel(value: number | string | undefined): string {
    if (typeof value === 'undefined') {
      return '-';
    }

    const numeric = typeof value === 'string' ? Number(value) : value;
    const map: Record<number, string> = {
      0: this.t('finance.customers.status.draft'),
      1: this.t('finance.customers.status.active'),
      2: this.t('finance.customers.status.suspended'),
      3: this.t('finance.customers.status.closed')
    };
    return map[numeric] ?? `${value}`;
  }

  partyStatusLabel(value: number | string): string {
    const numeric = typeof value === 'string' ? Number(value) : value;
    const map: Record<number, string> = {
      0: this.t('platform.parties.status.active'),
      1: this.t('platform.parties.status.inactive'),
      2: this.t('platform.parties.status.merged')
    };
    return map[numeric] ?? `${value}`;
  }

  kindLabel(value: number | string): string {
    const numeric = typeof value === 'string' ? Number(value) : value;
    const map: Record<number, string> = {
      0: this.t('platform.parties.kind.individual'),
      1: this.t('platform.parties.kind.organization'),
      2: this.t('platform.parties.kind.device'),
      3: this.t('platform.parties.kind.aiAgent'),
      4: this.t('platform.parties.kind.service')
    };
    return map[numeric] ?? `${value}`;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private buildInlinePartyPayload(): OnboardCustomerPayload['inlineParty'] {
    const legalName = this.inlineParty.legalName.trim() || undefined;
    const identifierValue = this.inlineParty.identifierValue.trim();
    const contactValue = this.inlineParty.contactValue.trim();

    return {
      legalName,
      kind: this.inlineParty.kind,
      identifier: identifierValue
        ? {
            identifierType: this.inlineParty.identifierType,
            value: identifierValue,
            issuingCountryCode: this.inlineParty.identifierCountryCode.trim() || undefined
          }
        : undefined,
      contact: contactValue
        ? {
            contactType: this.inlineParty.contactType,
            value: contactValue
          }
        : undefined
    };
  }

  private buildOnboardProfilesPayload(): OnboardCustomerProfilesPayload | undefined {
    const commercial = {
      priceTier: this.commercialForm.priceTier.trim() || undefined,
      discountCeilingPercent: this.toOptionalNumber(this.commercialForm.discountCeilingPercent),
      riskBand: this.commercialForm.riskBand.trim() || undefined,
      paymentBehaviorNotes: this.commercialForm.paymentBehaviorNotes.trim() || undefined
    };
    const credit = {
      creditLimit: this.toOptionalNumber(this.creditForm.creditLimit),
      exposureAmount: this.toOptionalNumber(this.creditForm.exposureAmount),
      isOverdue: this.creditForm.isOverdue,
      riskScore: this.toOptionalNumber(this.creditForm.riskScore)
    };
    const ai = {
      churnScore: this.toOptionalNumber(this.aiForm.churnScore),
      clvEstimate: this.toOptionalNumber(this.aiForm.clvEstimate),
      propensityBand: this.aiForm.propensityBand.trim() || undefined,
      nextBestActionNotes: this.aiForm.nextBestActionNotes.trim() || undefined
    };

    const hasCommercial = Object.values(commercial).some(value => value !== undefined && value !== '');
    const hasCredit = credit.creditLimit !== undefined || credit.exposureAmount !== undefined || credit.riskScore !== undefined || credit.isOverdue;
    const hasAi = Object.values(ai).some(value => value !== undefined && value !== '');

    if (!hasCommercial && !hasCredit && !hasAi) {
      return undefined;
    }

    return {
      commercial: hasCommercial ? commercial : undefined,
      credit: hasCredit ? credit : undefined,
      ai: hasAi ? ai : undefined
    };
  }

  private loadComplianceProfile(customerId: string): void {
    this.api.getPlatformCustomerComplianceProfile(customerId).subscribe({
      next: profile => this.complianceProfile.set(profile),
      error: () => this.complianceProfile.set(null)
    });
  }

  private loadCustomerProfiles(customerId: string): void {
    this.api.getPlatformCustomerCommercialProfile(customerId).subscribe({
      next: profile => {
        this.commercialProfile.set(profile);
        this.commercialForm = {
          priceTier: profile.priceTier ?? '',
          discountCeilingPercent: profile.discountCeilingPercent ?? '',
          riskBand: profile.riskBand ?? '',
          paymentBehaviorNotes: profile.paymentBehaviorNotes ?? ''
        };
      },
      error: () => this.commercialProfile.set(null)
    });

    this.api.getPlatformCustomerCreditProfile(customerId).subscribe({
      next: profile => {
        this.creditProfile.set(profile);
        this.creditForm = {
          creditLimit: profile.creditLimit ?? '',
          exposureAmount: profile.exposureAmount ?? '',
          riskScore: profile.riskScore ?? '',
          isOverdue: profile.isOverdue
        };
      },
      error: () => this.creditProfile.set(null)
    });

    this.api.getPlatformCustomerAiProfile(customerId).subscribe({
      next: profile => {
        this.aiProfile.set(profile);
        this.aiForm = {
          churnScore: profile.churnScore ?? '',
          clvEstimate: profile.clvEstimate ?? '',
          propensityBand: profile.propensityBand ?? '',
          nextBestActionNotes: profile.nextBestActionNotes ?? ''
        };
      },
      error: () => this.aiProfile.set(null)
    });
  }

  private toOptionalNumber(value: number | string): number | undefined {
    if (value === '' || value === null || typeof value === 'undefined') {
      return undefined;
    }

    const numeric = typeof value === 'string' ? Number(value) : value;
    return Number.isFinite(numeric) ? numeric : undefined;
  }

  private normalizeNumeric(value: number | string): number {
    return typeof value === 'string' ? Number(value) : value;
  }
}
